import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import t from 'react-translate';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import {
  CreateDataDownloadParams,
  CustomDataReportParams,
  DataDownloadDetailsResponse,
  DataDownloadsParams,
  DataDownloadsResponse,
  UpdateReportSchedulingParams,
} from 'redux/schemas/api/custom-downloads';
import { addAlertMessage } from './alert-messages';

export const toggleSftpReport = createAsyncThunk<boolean, CustomDataReportParams>(
  'TOGGLE_SFTP_REPORT',
  async ({ reportId, catalogId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${catalogId}/course_custom_data_report/${reportId}/toggle_sftp_report.json`);
      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return rejectWithValue(error);
    }
  },
);

export const toggleDailyReport = createAsyncThunk<boolean, CustomDataReportParams>(
  'TOGGLE_DAILY_REPORT',
  async ({ catalogId, reportId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${catalogId}/course_custom_data_report/${reportId}/toggle_daily_email.json`);
      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return rejectWithValue(error);
    }
  },
);

export const updateReportScheduling = createAsyncThunk<any, UpdateReportSchedulingParams>(
  'UPDATE_REPORT_SCHEDULING',
  async ({ catalogId, reportId, courseCustomDataReport }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.patch(`${catalogId}/course_custom_data_report/${reportId}/update_report_scheduling.json`, { courseCustomDataReport });
      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return rejectWithValue(error);
    }
  },
);

/**
 * Learning Journey Data Downloads APIs
 */

export const getCustomDataReports = createAsyncThunk<DataDownloadsResponse, DataDownloadsParams>(
  'GET_CUSTOM_DATA_REPORTS',
  async ({ catalogId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get<{ result: DataDownloadsResponse }>(`${catalogId}/course_custom_data_report.json`);

      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));

      return rejectWithValue(error);
    }
  },
);

export const createCustomDataReport = createAsyncThunk<Record<string, unknown>, CreateDataDownloadParams>(
  'CREATE_CUSTOM_DATA_REPORT',
  async ({ catalogId, title }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post<{ result: Record<string, unknown> }>(
        `${catalogId}/journey/course_custom_data_role_report.json`,
        {
          customReports: {
            title,
          },
        },
      );

      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));

      return rejectWithValue(error);
    }
  },
);

export const getCustomDataReport = createAsyncThunk<DataDownloadDetailsResponse, CustomDataReportParams>(
  'GET_CUSTOM_DATA_REPORT',
  async ({ catalogId, reportId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get<{ result: DataDownloadDetailsResponse }>(`${catalogId}/course_custom_data_report/${reportId}.json`);

      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));

      return rejectWithValue(error);
    }
  },
);

export const generateCustomDataReport = createAsyncThunk<Record<string, unknown>, CustomDataReportParams>(
  'GENERATE_CUSTOM_DATA_REPORT',
  async ({ catalogId, reportId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post<{ result: Record<string, unknown> }>(
        `${catalogId}/course_custom_data_role_report/generate_report.json`,
        { id: reportId },
      );

      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));

      return rejectWithValue(error);
    }
  },
);

export const updateCustomDataReport = createAsyncThunk<any, any>(
  'UPDATE_CUSTOM_DATA_REPORT',
  async ({ catalogId, reportId, ...updateParams }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.patch(`${catalogId}/course_custom_data_report/${reportId}.json`, updateParams);

      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));

      return rejectWithValue(error);
    }
  },
);

export const deleteCustomDataReport = createAsyncThunk<boolean, { catalogId: string, reportId: number }>(
  'DELETE_CUSTOM_DATA_REPORT',
  async ({ catalogId, reportId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${catalogId}/course_custom_data_role_report/delete.json`, { id: reportId });
      return response.data.result;
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return rejectWithValue(error);
    }
  },
);
